/**
 * views/ManagerConsole.tsx — /dashboard/operations
 * ──────────────────────────────────────────────────
 * Operations Manager console — tile grid + inline sections.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth }      from '@/contexts/AuthContext';
import { useWarehouse } from '@/contexts/WarehouseContext';
import { apiClient, ApiError } from '@/api/client';
import { InfiniumMark }       from '@/components/ui/InfiniumLogo';
import { WarehouseSwitcher }  from '@/components/ui/WarehouseSwitcher';
import type { AisleStats, Mission, MissionStatus, MissionSummary, WarehouseItem } from '@/types';
import WarehouseManagement from '@/views/WarehouseManagement';
import MissionPage         from '@/views/MissionPage';
import MainDashboard       from '@/views/MainDashboard';
import DetectionLab        from '@/views/DetectionLab';
import CartonLab           from '@/views/CartonLab';
import CustomerAccess      from '@/views/CustomerAccess';
import LemWorklist         from '@/views/LemWorklist';

// ── Types ─────────────────────────────────────────────────────────────────────

interface IngestionSummary {
  rows_parsed: number;
  rows_accepted: number;
  rows_rejected: number;
  rows_inserted: number;
  rows_updated?: number;
  validation_errors?: { row: number; field: string; message: string }[];
}

// ── Shared warehouse datalist ─────────────────────────────────────────────────
// Reads the warehouse list from WarehouseContext (already fetched at mount).
// No module-level cache needed — context is the single source of truth.

function WarehouseSelect({ id, value, onChange, disabled, required, placeholder }: {
  id: string; value: string;
  onChange: (uuid: string) => void;
  disabled?: boolean; required?: boolean; placeholder?: string;
}) {
  const { warehouses } = useWarehouse();
  const [display, setDisplay] = useState('');

  // Keep display text in sync when the controlled value changes externally
  // (e.g. pre-populated from context on mount).
  useEffect(() => {
    if (!value) { setDisplay(''); return; }
    const match = warehouses.find(w => w.id === value);
    if (match) setDisplay(`${match.name} (${match.code})`);
    else setDisplay(value); // fallback: show raw UUID
  }, [value, warehouses]);

  const handleChange = (text: string) => {
    setDisplay(text);
    const label = text.trim();
    const match = warehouses.find(
      (w: WarehouseItem) => `${w.name} (${w.code})` === label || w.name === label,
    );
    onChange(match ? match.id : label);
  };

  const listId = `wh-list-${id}`;
  return (
    <>
      <input
        id={id}
        className="form-input"
        list={listId}
        placeholder={placeholder ?? 'Select a warehouse or type a UUID'}
        value={display}
        onChange={e => handleChange(e.target.value)}
        disabled={disabled}
        required={required}
        autoComplete="off"
      />
      <datalist id={listId}>
        {warehouses.map((w: WarehouseItem) => (
          <option key={w.id} value={`${w.name} (${w.code})`} />
        ))}
      </datalist>
    </>
  );
}

// ── Sub-sections ──────────────────────────────────────────────────────────────

// ── Shared status card types ──────────────────────────────────────────────────

interface SkuVersionRecord {
  upload_id: string; filename: string; rows_accepted: number;
  rows_rejected: number; is_current: boolean; uploaded_at: string;
  uploaded_by_name?: string; uploaded_by_email?: string;
}

function fmt(iso: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function SkuStatusCard({ version }: { version: SkuVersionRecord | null | undefined }) {
  if (version === undefined) return <div className="upload-status-card">Loading current SKU version…</div>;
  if (!version) return (
    <div className="upload-status-card upload-status-card--empty">
      <span className="us-icon">📭</span>
      <span>No SKU master uploaded yet for this warehouse.</span>
    </div>
  );
  return (
    <div className="upload-status-card upload-status-card--active">
      <div className="us-header">
        <span className="us-badge us-badge--active">● Current SKU Master</span>
        <span className="us-filename" title={version.filename}>{version.filename}</span>
      </div>
      <div className="us-meta-grid">
        <div className="us-meta-item"><span className="us-meta-lbl">SKUs loaded</span><strong>{version.rows_accepted.toLocaleString()}</strong></div>
        <div className="us-meta-item"><span className="us-meta-lbl">Rejected</span><strong className={version.rows_rejected > 0 ? 'us-err' : 'us-ok'}>{version.rows_rejected.toLocaleString()}</strong></div>
        <div className="us-meta-item"><span className="us-meta-lbl">Uploaded</span><strong>{fmt(version.uploaded_at)}</strong></div>
        <div className="us-meta-item"><span className="us-meta-lbl">By</span><strong>{version.uploaded_by_name ?? version.uploaded_by_email ?? '—'}</strong></div>
      </div>
    </div>
  );
}

/** Pull a human-friendly error + optional column hint from an ApiError */
function ingestionErrorInfo(err: unknown): { message: string; hints: { col: string; aliases: string[] }[] } {
  if (!(err instanceof ApiError)) return { message: 'Upload failed.', hints: [] };
  const inner = (err.detail as Record<string, unknown> | undefined)?.detail as Record<string, unknown> | undefined;
  const missing: string[]  = Array.isArray(inner?.missing_columns)  ? inner!.missing_columns  as string[] : [];
  const supported: Record<string, string[]> =
    inner?.supported_column_names && typeof inner.supported_column_names === 'object'
      ? inner.supported_column_names as Record<string, string[]>
      : {};
  const hints = missing.map(col => ({ col, aliases: supported[col] ?? [] }));
  return { message: err.message, hints };
}

function SkuUpload() {
  const { activeWarehouseId } = useWarehouse();
  const [warehouseId, setWarehouseId] = useState(activeWarehouseId ?? '');
  const [file, setFile]               = useState<File | null>(null);
  const [busy, setBusy]               = useState(false);
  const [result, setResult]           = useState<IngestionSummary | null>(null);
  const [error, setError]             = useState<string | null>(null);
  const [colHints, setColHints]       = useState<{ col: string; aliases: string[] }[]>([]);
  const [skuVersion, setSkuVersion]   = useState<SkuVersionRecord | null | undefined>(undefined);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadSkuVersion = async (whId: string) => {
    if (!whId.trim() || whId.length < 10) { setSkuVersion(null); return; }
    try {
      const data = await apiClient.get<{ warehouse_id: string; versions: SkuVersionRecord[] }>(
        `/api/manage/sku-versions/${whId}`
      );
      setSkuVersion(data.versions.find(v => v.is_current) ?? data.versions[0] ?? null);
    } catch { setSkuVersion(null); }
  };

  useEffect(() => { loadSkuVersion(warehouseId); }, [warehouseId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true); setResult(null); setError(null); setColHints([]);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (warehouseId.trim()) fd.append('warehouse_id', warehouseId.trim());
      const data = await apiClient.postForm('/api/manage/upload-sku', fd) as IngestionSummary;
      setResult(data);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      await loadSkuVersion(warehouseId);
    } catch (err) {
      const { message, hints } = ingestionErrorInfo(err);
      setError(message);
      setColHints(hints);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mgr-section-form">
      <SkuStatusCard version={skuVersion} />
      <h3 className="mgr-subsection-title">Upload new SKU master</h3>
      <form onSubmit={submit} style={{ display: 'contents' }}>
      <div className="form-field">
        <label className="form-label">Warehouse — optional (leave blank for global SKU rules)</label>
        <WarehouseSelect id="sku-wh" value={warehouseId} onChange={v => { setWarehouseId(v); setSkuVersion(undefined); }}
          disabled={busy} placeholder="Leave blank for global SKU rules" />
      </div>
      <div className="form-field">
        <label className="form-label">SKU Dimensions File (.xlsx / .csv)</label>
        <input ref={fileRef} className="form-input" type="file"
          accept=".xlsx,.xls,.xlsm,.csv"
          onChange={e => setFile(e.target.files?.[0] ?? null)} required disabled={busy} />
      </div>
      {error && (
        <div className="form-error">
          <div>{error}</div>
          {colHints.length > 0 && (
            <div className="mgr-col-hints">
              <strong>Rename the missing column to one of these accepted names:</strong>
              {colHints.map(({ col, aliases }) => (
                <div key={col} className="mgr-col-hint-row">
                  <span className="mgr-col-hint-missing">{col}</span>
                  <span className="mgr-col-hint-sep">→ accepted:</span>
                  <span className="mgr-col-hint-aliases">{aliases.join(', ')}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {result && <IngestionResult data={result} />}
      <button className="btn btn--primary" type="submit" disabled={busy || !file}>
        {busy ? 'Uploading…' : 'Upload SKU File'}
      </button>
      </form>
    </div>
  );
}

interface UserRecord {
  id: string; email: string; name: string; company?: string;
  role: string; is_active: boolean; created_at: string; last_login?: string;
  warehouse_id?: string;
}

const ROLE_CHIP: Record<string, string> = {
  super_admin: 'mgr-chip--super-admin', admin: 'mgr-chip--admin',
  manager: 'mgr-chip--manager', pilot: 'mgr-chip--pilot', customer: 'mgr-chip--customer',
};

// ── Credentials reveal (shown once after user creation) ───────────────────────

function CredsReveal({
  email, password, onDismiss,
}: {
  email: string; password: string; onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="creds-reveal">
      <div className="creds-reveal-header">
        <span>✓ User created — save these credentials now</span>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onDismiss}>Dismiss</button>
      </div>
      <div className="creds-reveal-row">
        <span className="creds-reveal-lbl">Email</span>
        <code className="creds-reveal-val">{email}</code>
      </div>
      <div className="creds-reveal-row">
        <span className="creds-reveal-lbl">Password</span>
        <code className="creds-reveal-val">{password}</code>
        <button type="button" className="btn btn--ghost btn--sm" onClick={copy}>
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </div>
      <p className="creds-reveal-warn">⚠ This password will not be shown again.</p>
    </div>
  );
}

// ── Per-row password reset panel ──────────────────────────────────────────────

function _generatePassword(): string {
  // Excludes ambiguous chars (0/O, 1/I/l)
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789@#!';
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

function ResetPasswordPanel({ userId, onDone }: { userId: string; onDone: () => void }) {
  const [pw, setPw]         = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState<string | null>(null);
  const [done, setDone]     = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const copy = (val: string) => {
    navigator.clipboard.writeText(val);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const generate = () => {
    setPw(_generatePassword());
    setShowPw(true);
    setDone(null);
    setCopied(false);
  };

  const save = async () => {
    if (pw.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setBusy(true); setError(null);
    try {
      await apiClient.patch(`/api/auth/users/${userId}/password`, { new_password: pw });
      setDone(pw);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to reset password.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="reset-pw-panel reset-pw-panel--done">
        <span className="reset-pw-done-msg">✓ Password updated — share with the user:</span>
        <code className="creds-reveal-val">{done}</code>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => copy(done)}>
          {copied ? '✓ Copied' : 'Copy'}
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onDone}>Close</button>
      </div>
    );
  }

  return (
    <div className="reset-pw-panel">
      <div className="reset-pw-row">
        <div className="pw-wrap" style={{ flex: 1, minWidth: 0 }}>
          <input
            className="form-input"
            type={showPw ? 'text' : 'password'}
            placeholder="New password (min 8 chars)"
            value={pw}
            onChange={e => { setPw(e.target.value); setDone(null); }}
            disabled={busy}
          />
          <button
            type="button" className="pw-toggle"
            aria-label={showPw ? 'Hide password' : 'Show password'}
            onClick={() => setShowPw(v => !v)} tabIndex={-1}
          >
            {showPw ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            )}
          </button>
        </div>
        <button type="button" className="btn btn--ghost btn--sm" onClick={generate} disabled={busy}>
          ⚡ Generate
        </button>
        {pw && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => copy(pw)} disabled={busy}>
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        )}
        <button
          type="button" className="btn btn--primary btn--sm"
          onClick={save}
          disabled={busy || pw.length < 8}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onDone} disabled={busy}>
          Cancel
        </button>
      </div>
      {error && <div className="form-error" style={{ marginTop: 4, fontSize: '.8125rem' }}>{error}</div>}
    </div>
  );
}

function CreateUser() {
  const [form, setForm] = useState({ email: '', name: '', company: '', password: '', role: 'pilot', warehouse_id: '' });
  const [busy, setBusy]       = useState(false);
  const [showPw, setShowPw]   = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [users, setUsers]           = useState<UserRecord[] | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [newCreds, setNewCreds]     = useState<{ email: string; password: string } | null>(null);
  const [resetUserId, setResetUserId] = useState<string | null>(null);

  const loadUsers = async () => {
    try {
      const data = await apiClient.get('/api/auth/users') as UserRecord[];
      setUsers(data);
    } catch { /* silently ignore */ }
  };

  const toggleActive = async (u: UserRecord) => {
    setTogglingId(u.id);
    try {
      const updated = await apiClient.patch(`/api/auth/users/${u.id}/active`, { is_active: !u.is_active }) as UserRecord;
      setUsers(prev => prev ? prev.map(x => x.id === updated.id ? updated : x) : prev);
    } catch { /* silently ignore */ } finally {
      setTogglingId(null);
    }
  };

  // Load on mount
  useEffect(() => { loadUsers(); }, []);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setBusy(true); setError(null); setNewCreds(null);
    const capturedEmail = form.email;
    const capturedPw    = form.password;
    try {
      const body: Record<string, string> = { email: form.email, name: form.name, password: form.password, role: form.role };
      if (form.company.trim())      body.company      = form.company.trim();
      if (form.warehouse_id.trim()) body.warehouse_id = form.warehouse_id.trim();
      await apiClient.post('/api/auth/users', body);
      setNewCreds({ email: capturedEmail, password: capturedPw });
      setForm({ email: '', name: '', company: '', password: '', role: 'pilot', warehouse_id: '' });
      setShowPw(false);
      loadUsers(); // refresh count
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create user.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mgr-users-wrap">
      {/* ── User count + list ── */}
      <div className="mgr-user-count">
        {users === null ? 'Loading…' : (
          <><b>{users.length}</b> registered user{users.length !== 1 ? 's' : ''}</>
        )}
      </div>
      {users && users.length > 0 && (
        <div className="mgr-user-table-wrap">
          <table className="mgr-user-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Company</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th>Last login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <React.Fragment key={u.id}>
                  <tr className={u.is_active ? '' : 'mgr-row--disabled'}>
                    <td>{u.name || '—'}</td>
                    <td>{u.email}</td>
                    <td>{u.company || '—'}</td>
                    <td><span className={`mgr-chip ${ROLE_CHIP[u.role] ?? ''}`}>{u.role}</span></td>
                    <td className={u.is_active ? 'mgr-td--ok' : 'mgr-td--err'}>
                      {u.is_active ? 'Active' : 'Disabled'}
                    </td>
                    <td>{new Date(u.created_at).toLocaleDateString()}</td>
                    <td>{u.last_login ? new Date(u.last_login).toLocaleDateString() : '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          className={`mgr-toggle-btn ${u.is_active ? 'mgr-toggle-btn--disable' : 'mgr-toggle-btn--enable'}`}
                          onClick={() => toggleActive(u)}
                          disabled={togglingId === u.id}
                        >
                          {togglingId === u.id ? '…' : u.is_active ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          className={`mgr-toggle-btn mgr-toggle-btn--reset${resetUserId === u.id ? ' mgr-toggle-btn--reset-active' : ''}`}
                          onClick={() => setResetUserId(prev => prev === u.id ? null : u.id)}
                        >
                          {resetUserId === u.id ? '✕ Cancel' : '🔑 Reset PW'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {resetUserId === u.id && (
                    <tr className="mgr-row--reset-panel">
                      <td colSpan={8}>
                        <ResetPasswordPanel userId={u.id} onDone={() => setResetUserId(null)} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 className="mgr-subsection-title">Create new user</h3>
      <form className="mgr-section-form" onSubmit={submit}>
      <div className="mgr-form-row">
        <div className="form-field">
          <label className="form-label">Email</label>
          <input className="form-input" type="email" value={form.email} onChange={set('email')} required disabled={busy} />
        </div>
        <div className="form-field">
          <label className="form-label">Full Name</label>
          <input className="form-input" value={form.name} onChange={set('name')} required disabled={busy} />
        </div>
      </div>
      <div className="mgr-form-row">
        <div className="form-field">
          <label className="form-label">Company</label>
          <input className="form-input" value={form.company} onChange={set('company')} disabled={busy} />
        </div>
        <div className="form-field">
          <label className="form-label">Role</label>
          <select className="form-input" value={form.role} onChange={set('role')} disabled={busy}>
            <option value="pilot">Pilot</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
            <option value="customer">Customer</option>
          </select>
        </div>
      </div>
      <div className="mgr-form-row">
        <div className="form-field">
          <label className="form-label">Password</label>
          <div className="pw-wrap">
            <input className="form-input" type={showPw ? 'text' : 'password'}
              value={form.password} onChange={set('password')} required disabled={busy} />
            <button type="button" className="pw-toggle"
              aria-label={showPw ? 'Hide password' : 'Show password'}
              onClick={() => setShowPw(v => !v)} tabIndex={-1}>
              {showPw ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              )}
            </button>
          </div>
        </div>
        <div className="form-field">
          <label className="form-label">Warehouse — optional</label>
          <WarehouseSelect id="user-wh" value={form.warehouse_id}
            onChange={v => setForm(f => ({ ...f, warehouse_id: v }))} disabled={busy} />
        </div>
      </div>
      {error    && <div className="form-error">{error}</div>}
      {newCreds && (
        <CredsReveal
          email={newCreds.email}
          password={newCreds.password}
          onDismiss={() => setNewCreds(null)}
        />
      )}
      <button className="btn btn--primary" type="submit" disabled={busy}>
        {busy ? 'Creating…' : 'Create User'}
      </button>
      </form>
    </div>
  );
}

// ── Analytics types ───────────────────────────────────────────────────────────

interface AisleRow {
  aisle: string;
  total_count: number;
  match_count: number;
  discrepancy_count: number;
}

interface MissionRecordsResponse {
  mission_id: string;
  aisles: AisleRow[];
  summary: {
    total: number; match: number; mismatch_sku: number;
    discrepancy_count: number; empty_slot: number;
    detection_failure: number; requires_retake: number; requires_review: number;
  };
}

// ── Live-feed event ───────────────────────────────────────────────────────────
interface LiveEvent {
  event: string;
  photo_id?: string;
  data?: Record<string, unknown>;
  receivedAt: string;
}

const FLAG_CHIP: Record<string, string> = {
  MATCH:             'mgr-chip--pilot',
  DISCREPANCY_COUNT: 'mgr-chip--manager',
  MISMATCH_SKU:      'mgr-chip--super-admin',
  EMPTY_SLOT:        'mgr-chip--customer',
  DETECTION_FAILURE: '',
};

// ── Mission status helpers ────────────────────────────────────────────────────

const STATUS_CHIP: Record<MissionStatus, string> = {
  PENDING:   'mgr-chip--customer',
  ACTIVE:    'mgr-chip--pilot',
  COMPLETED: 'mgr-chip--admin',
  CANCELLED: '',
};

function fmtDateTime(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

// ── Create Mission sub-form ───────────────────────────────────────────────────

interface PilotUser { id: string; name: string; email: string; }

function CreateMission({ onCreated }: { onCreated: () => void }) {
  const { activeWarehouseId, warehouses } = useWarehouse();

  const [title,        setTitle]        = useState('');
  const [warehouseId,  setWarehouseId]  = useState(activeWarehouseId ?? '');
  const [pilotIds,     setPilotIds]     = useState<Set<string>>(new Set());
  const [startDate,    setStartDate]    = useState('');
  const [endDate,      setEndDate]      = useState('');
  const [notes,        setNotes]        = useState('');
  const [pilots,       setPilots]       = useState<PilotUser[]>([]);
  const [busy,         setBusy]         = useState(false);
  const [error,        setError]        = useState<string | null>(null);
  const [success,      setSuccess]      = useState<string | null>(null);

  // ── Aisle picker ──
  const [aisleData,      setAisleData]      = useState<AisleStats[]>([]);
  const [aislesLoading,  setAislesLoading]  = useState(false);
  const [selectedAisles, setSelectedAisles] = useState<Set<string>>(new Set());
  const [showVerify,     setShowVerify]     = useState(false);

  // Sync warehouse field when context changes
  useEffect(() => {
    if (activeWarehouseId && !warehouseId) setWarehouseId(activeWarehouseId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWarehouseId]);

  // Load aisles when warehouse changes
  useEffect(() => {
    if (!warehouseId) { setAisleData([]); setSelectedAisles(new Set()); return; }
    setAislesLoading(true);
    setSelectedAisles(new Set());
    setShowVerify(false);
    apiClient.get<AisleStats[]>(`/api/manage/warehouses/${warehouseId}/aisles`)
      .then(data => setAisleData(data))
      .catch(() => setAisleData([]))
      .finally(() => setAislesLoading(false));
  }, [warehouseId]);

  // Load pilots once
  useEffect(() => {
    apiClient.get<{ id: string; name: string; email: string; role: string }[]>('/api/auth/users')
      .then(users => setPilots(users.filter(u => u.role === 'pilot')))
      .catch(() => {});
  }, []);

  const toggleAisle = (aisle: string) => {
    setSelectedAisles(prev => {
      const next = new Set(prev);
      next.has(aisle) ? next.delete(aisle) : next.add(aisle);
      return next;
    });
    setShowVerify(false);
  };

  const toggleAll = () => {
    if (selectedAisles.size === aisleData.length) {
      setSelectedAisles(new Set());
    } else {
      setSelectedAisles(new Set(aisleData.map(a => a.aisle)));
    }
    setShowVerify(false);
  };

  const selectedLocationCount = aisleData
    .filter(a => selectedAisles.has(a.aisle))
    .reduce((sum, a) => sum + a.location_count, 0);

  const selectedLocations = aisleData
    .filter(a => selectedAisles.has(a.aisle))
    .flatMap(a => a.locations);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null); setSuccess(null);
    try {
      await apiClient.post<Mission>('/api/missions', {
        title: title.trim(),
        warehouse_id: warehouseId.trim(),
        pilot_ids: [...pilotIds],
        allocated_aisles: [...selectedAisles],
        scheduled_start: startDate || null,
        scheduled_end:   endDate   || null,
        notes: notes.trim() || null,
      });
      setSuccess(`Mission "${title.trim()}" created.`);
      setTitle(''); setPilotIds(new Set());
      setSelectedAisles(new Set()); setShowVerify(false);
      setStartDate(''); setEndDate(''); setNotes('');
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create mission.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="mgr-section-form" onSubmit={submit}>
      {/* Title */}
      <div className="form-field">
        <label className="form-label">Mission title *</label>
        <input
          className="form-input"
          placeholder="e.g. Week 21 — Aisles A–D"
          value={title}
          onChange={e => setTitle(e.target.value)}
          required disabled={busy}
        />
      </div>

      {/* Warehouse */}
      <div className="form-field">
        <label className="form-label">Warehouse *</label>
        <select
          className="form-input"
          value={warehouseId}
          onChange={e => setWarehouseId(e.target.value)}
          required disabled={busy}
        >
          <option value="">— Select warehouse —</option>
          {warehouses.map(w => (
            <option key={w.id} value={w.id}>{w.name} ({w.code})</option>
          ))}
        </select>
      </div>

      {/* Pilot assignment — many-to-many: several pilots share one mission/report */}
      <div className="form-field">
        <label className="form-label">
          Assign pilots{pilotIds.size > 0 ? ` (${pilotIds.size})` : ''}
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {pilots.length === 0 ? (
            <span style={{ fontStyle: 'italic', opacity: 0.6, fontSize: '13px' }}>
              No pilots available
            </span>
          ) : (
            pilots.map(p => {
              const on = pilotIds.has(p.id);
              return (
                <button
                  type="button"
                  key={p.id}
                  disabled={busy}
                  title={p.email}
                  onClick={() => setPilotIds(prev => {
                    const next = new Set(prev);
                    if (next.has(p.id)) next.delete(p.id); else next.add(p.id);
                    return next;
                  })}
                  style={{
                    cursor: busy ? 'default' : 'pointer',
                    border: '1px solid',
                    borderColor: on ? 'var(--primary, #2563eb)' : 'rgba(120,120,120,0.4)',
                    background: on ? 'rgba(37,99,235,0.12)' : 'transparent',
                    color: on ? 'var(--primary, #2563eb)' : 'inherit',
                    fontWeight: on ? 600 : 400,
                    borderRadius: '999px',
                    padding: '2px 10px',
                    fontSize: '13px',
                  }}
                >
                  {on ? '✓ ' : ''}{p.name}
                </button>
              );
            })
          )}
        </div>
        <p style={{ fontSize: '12px', opacity: 0.7, marginTop: '4px' }}>
          {pilotIds.size === 0
            ? 'Unassigned — any pilot of the warehouse can fly it.'
            : `${pilotIds.size} pilot${pilotIds.size > 1 ? 's' : ''} share this mission and its single report.`}
        </p>
      </div>

      {/* Allocated aisles — structured picker */}
      <div className="form-field">
        <label className="form-label">Allocated aisles</label>

        {!warehouseId && (
          <p className="aisle-picker-hint">Select a warehouse above to load aisles.</p>
        )}

        {warehouseId && aislesLoading && (
          <p className="aisle-picker-hint">Loading aisles…</p>
        )}

        {warehouseId && !aislesLoading && aisleData.length === 0 && (
          <p className="aisle-picker-hint">No location master uploaded for this warehouse.</p>
        )}

        {warehouseId && !aislesLoading && aisleData.length > 0 && (
          <div className="aisle-picker">
            {/* Select all toggle */}
            <label className="aisle-picker-selectall">
              <input
                type="checkbox"
                checked={selectedAisles.size === aisleData.length}
                onChange={toggleAll}
                disabled={busy}
              />
              <span>
                {selectedAisles.size === aisleData.length
                  ? `Deselect all (${aisleData.length} aisles)`
                  : `Select all aisles (${aisleData.length})`}
              </span>
            </label>

            {/* Aisle checkboxes */}
            <div className="aisle-picker-grid">
              {aisleData.map(a => (
                <label
                  key={a.aisle}
                  className={`aisle-chip-label ${selectedAisles.has(a.aisle) ? 'aisle-chip-label--selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedAisles.has(a.aisle)}
                    onChange={() => toggleAisle(a.aisle)}
                    disabled={busy}
                  />
                  <span className="aisle-chip-name">{a.aisle}</span>
                  <span className="aisle-chip-count">{a.location_count}</span>
                </label>
              ))}
            </div>

            {/* Running total + verify */}
            {selectedAisles.size > 0 && (
              <div className="aisle-picker-summary">
                <span className="aisle-picker-total">
                  <strong>{selectedAisles.size}</strong> aisle{selectedAisles.size !== 1 ? 's' : ''}
                  {' '}selected ·{' '}
                  <strong>{selectedLocationCount.toLocaleString()}</strong> locations
                </span>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setShowVerify(v => !v)}
                >
                  {showVerify ? 'Hide locations ▲' : 'Verify locations ▼'}
                </button>
              </div>
            )}

            {selectedAisles.size === 0 && (
              <p className="aisle-picker-hint" style={{ marginTop: 8 }}>
                Leave all unchecked = no scope restriction (pilot can scan any location).
              </p>
            )}

            {/* Location verification panel */}
            {showVerify && selectedLocations.length > 0 && (
              <div className="aisle-verify-panel">
                <div className="aisle-verify-header">
                  {selectedLocationCount.toLocaleString()} location codes across {selectedAisles.size} aisle{selectedAisles.size !== 1 ? 's' : ''}
                </div>
                <div className="aisle-verify-grid">
                  {selectedLocations.map(loc => (
                    <code key={loc} className="aisle-verify-loc">{loc}</code>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Schedule */}
      <div className="form-row-2">
        <div className="form-field">
          <label className="form-label">Scheduled start</label>
          <input
            className="form-input"
            type="datetime-local"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="form-field">
          <label className="form-label">Scheduled end</label>
          <input
            className="form-input"
            type="datetime-local"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>

      {/* Notes */}
      <div className="form-field">
        <label className="form-label">Notes (optional)</label>
        <input
          className="form-input"
          placeholder="Any briefing notes for the pilot"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          disabled={busy}
        />
      </div>

      {error   && <div className="form-error">{error}</div>}
      {success && <div className="form-success">{success}</div>}

      <button className="btn btn--primary" type="submit" disabled={busy || !title.trim() || !warehouseId}>
        {busy ? 'Creating…' : '+ Create Mission'}
      </button>
    </form>
  );
}

// ── Flagged record types ──────────────────────────────────────────────────────

interface FlaggedRecordMgr {
  id: string;
  location_code: string;
  flag: string;
  reasoning: string;
  requires_retake: boolean;
  requires_review: boolean;
  pilot_note: string | null;
  pilot_note_at: string | null;
  review_status: string;   // 'pending' | 'approved' | 'rejected'
  reviewer_note: string | null;
  reviewed_at: string | null;
}

const REVIEW_STATUS_CLS: Record<string, string> = {
  pending:  'mgr-chip--customer',
  approved: 'mgr-chip--pilot',
  rejected: 'mgr-chip--super-admin',
};

// ── Inline flagged-records review panel ───────────────────────────────────────

function MissionReviewPanel({ missionId }: { missionId: string }) {
  const [records, setRecords]       = useState<FlaggedRecordMgr[] | null>(null);
  const [error, setError]           = useState<string | null>(null);
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [noteInputs, setNoteInputs] = useState<Record<string, string>>({});

  const load = async () => {
    try {
      const data = await apiClient.get<FlaggedRecordMgr[]>(
        `/api/manage/missions/${missionId}/flagged`,
      );
      setRecords(data);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load flagged records.');
    }
  };

  useEffect(() => { load(); }, [missionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (recordId: string, decision: 'approved' | 'rejected') => {
    setDecidingId(recordId);
    try {
      await apiClient.patch(`/api/manage/records/${recordId}/review`, {
        decision,
        reviewer_note: noteInputs[recordId]?.trim() || null,
      });
      await load(); // refresh
    } catch { /* silently ignore */ } finally {
      setDecidingId(null);
    }
  };

  if (error) return <div className="form-error" style={{ margin: '8px 0' }}>{error}</div>;
  if (!records) return <div className="mgr-review-loading">Loading flagged records…</div>;
  if (records.length === 0) return (
    <div className="mgr-review-empty">✅ No flagged records — all locations clean.</div>
  );

  const pending  = records.filter(r => r.review_status === 'pending').length;
  const approved = records.filter(r => r.review_status === 'approved').length;
  const rejected = records.filter(r => r.review_status === 'rejected').length;

  return (
    <div className="mgr-review-panel">
      <div className="mgr-review-summary">
        <span>{records.length} flagged ·</span>
        <span className="mgr-review-pending">{pending} pending</span>
        <span>· {approved} approved · {rejected} rejected</span>
      </div>

      <div className="mgr-review-list">
        {records.map(r => (
          <div key={r.id} className={`mgr-review-record mgr-review-record--${r.review_status}`}>
            <div className="mgr-review-record-top">
              <code className="mgr-review-loc">{r.location_code}</code>
              <span className={`mgr-chip ${FLAG_CHIP[r.flag] ?? ''}`}>{r.flag.replace(/_/g, ' ')}</span>
              <span className={`mgr-chip ${REVIEW_STATUS_CLS[r.review_status] ?? ''}`}>
                {r.review_status}
              </span>
            </div>

            <p className="mgr-review-reason">{r.reasoning}</p>

            {r.pilot_note && (
              <div className="mgr-review-pilot-note">
                <span className="mgr-review-note-lbl">Pilot note:</span> {r.pilot_note}
              </div>
            )}

            {r.review_status === 'pending' && (
              <div className="mgr-review-actions">
                <input
                  className="form-input mgr-review-note-input"
                  placeholder="Optional reviewer note…"
                  value={noteInputs[r.id] ?? ''}
                  onChange={e => setNoteInputs(p => ({ ...p, [r.id]: e.target.value }))}
                  disabled={decidingId === r.id}
                />
                <button
                  className="btn btn--primary btn--sm"
                  onClick={() => decide(r.id, 'approved')}
                  disabled={decidingId === r.id}
                >
                  ✓ Approve
                </button>
                <button
                  className="btn btn--ghost btn--sm"
                  style={{ color: 'var(--clr-danger)' }}
                  onClick={() => decide(r.id, 'rejected')}
                  disabled={decidingId === r.id}
                >
                  ✕ Reject
                </button>
              </div>
            )}

            {r.reviewer_note && (
              <p className="mgr-review-reviewer-note">📝 {r.reviewer_note}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Mission list card ─────────────────────────────────────────────────────────

function MissionRow({
  mission,
  scanSummary,
  onStatusChange,
}: {
  mission: Mission;
  scanSummary: MissionSummary | null;
  onStatusChange: () => void;
}) {
  const [busy, setBusy]             = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  const setStatus = async (newStatus: string) => {
    setBusy(true);
    try {
      await apiClient.patch(`/api/missions/${mission.id}`, { status: newStatus });
      onStatusChange();
    } catch { /* silently ignore */ } finally {
      setBusy(false);
    }
  };

  // Run reconciliation against the ACTIVE SOH — same endpoint the pilot
  // "Process Report" button calls. Idempotent (upsert per location); a frozen
  // mission is rejected server-side with a clear message.
  const runReconcile = async () => {
    setReconciling(true);
    try {
      const res = await apiClient.post(
        `/api/missions/${mission.id}/reconcile`, null,
      ) as { records_written: number; skipped: number };
      alert(
        res.records_written > 0
          ? `Report processed — ${res.records_written.toLocaleString()} locations reconciled.`
          : 'No assigned photos to report on yet.'
      );
      onStatusChange();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Reconcile failed — check your connection.');
    } finally {
      setReconciling(false);
    }
  };

  const matchPct = scanSummary && scanSummary.record_count > 0
    ? Math.round((scanSummary.match_count / scanSummary.record_count) * 100)
    : null;

  const pendingReviews = scanSummary?.review_count ?? 0;

  return (
    <div className="mgr-mission-row">
      <div className="mgr-mission-row-top">
        <span className={`mgr-chip ${STATUS_CHIP[mission.status]}`}>{mission.status}</span>
        <span className="mgr-mission-title">
          {mission.title ?? <code className="mgr-mission-id">{mission.id.slice(0, 8)}…</code>}
        </span>
        {matchPct !== null && (
          <span
            className={`mgr-chip ${matchPct >= 90 ? 'mgr-chip--pilot' : matchPct >= 70 ? 'mgr-chip--manager' : 'mgr-chip--super-admin'}`}
            title="Match rate from scan data"
          >
            {matchPct}% match
          </span>
        )}
      </div>

      <div className="mgr-mission-meta">
        {mission.warehouse_name && (
          <span className="mgr-mission-meta-item">🏭 {mission.warehouse_name}</span>
        )}
        {mission.pilots && mission.pilots.length > 0 ? (
          <span className="mgr-mission-meta-item">
            👤 {mission.pilots.map(p => p.name ?? p.email ?? p.id.slice(0, 8)).join(', ')}
          </span>
        ) : mission.pilot_name ? (
          <span className="mgr-mission-meta-item">👤 {mission.pilot_name}</span>
        ) : null}
        {(mission.scheduled_start || mission.scheduled_end) && (
          <span className="mgr-mission-meta-item">
            🕐 {fmtDateTime(mission.scheduled_start)} → {fmtDateTime(mission.scheduled_end)}
          </span>
        )}
        {scanSummary && (
          <span className="mgr-mission-meta-item">
            📍 {scanSummary.record_count.toLocaleString()} scanned
            {scanSummary.discrepancy_count > 0 && ` · ${scanSummary.discrepancy_count} discrepancies`}
            {scanSummary.retake_count > 0 && ` · ${scanSummary.retake_count} retakes`}
          </span>
        )}
      </div>

      {mission.allocated_aisles.length > 0 && (
        <div className="mgr-mission-aisles">
          <span className="mgr-mission-aisles-lbl">Aisles:</span>
          {[...mission.allocated_aisles].sort().map(a => (
            <span key={a} className="ms-aisle-chip">{a}</span>
          ))}
        </div>
      )}

      {mission.notes && (
        <p className="mgr-mission-notes">{mission.notes}</p>
      )}

      <div className="mgr-mission-actions">
        {mission.status === 'PENDING' && (
          <button className="btn btn--primary btn--sm" onClick={() => setStatus('ACTIVE')} disabled={busy}>
            → Activate
          </button>
        )}
        {mission.status === 'ACTIVE' && (
          <button className="btn btn--ghost btn--sm" onClick={() => setStatus('COMPLETED')} disabled={busy}>
            ✓ Mark complete
          </button>
        )}
        {(mission.status === 'PENDING' || mission.status === 'ACTIVE') && (
          <button className="btn btn--ghost btn--sm" onClick={() => setStatus('CANCELLED')} disabled={busy}>
            ✕ Cancel
          </button>
        )}
        {/* Process Report — run reconciliation against the active SOH.
            Same endpoint as the pilot-side button; managers/admins previously
            had no way to trigger it from this console. */}
        <button
          className="btn btn--primary btn--sm"
          onClick={runReconcile}
          disabled={reconciling || busy}
          title="Run reconciliation on assigned photos against the active SOH"
        >
          {reconciling ? '⏳ Processing…' : '📊 Process Report'}
        </button>
        {/* Triage link — always available */}
        <Link
          to={`/dashboard/triage/${mission.id}`}
          className="btn btn--ghost btn--sm"
          style={{ textDecoration: 'none' }}
          title="Open exception triage for this mission"
        >
          🔍 Triage
        </Link>
        {/* Inventory report — shown once scan data exists */}
        {scanSummary && scanSummary.record_count > 0 && (
          <Link
            to={`/dashboard/report/${mission.id}`}
            state={{ title: mission.title }}
            className="btn btn--ghost btn--sm"
            style={{ textDecoration: 'none' }}
            title="View the reconciliation report for this mission"
          >
            📊 View Report
          </Link>
        )}
        {/* Review drill-down — shown when scan data exists */}
        {scanSummary && (scanSummary.review_count > 0 || scanSummary.retake_count > 0) && (
          <button
            className={`btn btn--sm ${showReview ? 'btn--primary' : 'btn--ghost'}`}
            onClick={() => setShowReview(v => !v)}
          >
            {pendingReviews > 0
              ? `📋 Review (${pendingReviews})`
              : `📋 Review`}
          </button>
        )}
      </div>

      {/* Inline review panel */}
      {showReview && <MissionReviewPanel missionId={mission.id} />}
    </div>
  );
}

// ── Missions main section ─────────────────────────────────────────────────────

export function Missions() {
  const { activeWarehouseId }   = useWarehouse();
  const [tab, setTab]           = useState<'list' | 'create'>('list');
  const [mgmtMissions, setMgmtMissions] = useState<Mission[] | null>(null);
  const [mgmtError, setMgmtError]       = useState<string | null>(null);

  // Scan-results summary (reconciliation data — kept for the stats panel)
  const [scanSummary, setScanSummary]   = useState<MissionSummary[] | null>(null);

  const [sessionId, setSessionId]   = useState('');
  const [wsConnected, setWsConnected] = useState(false);
  const [liveEvents, setLiveEvents] = useState<LiveEvent[]>([]);
  const wsRef       = useRef<WebSocket | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadMgmt = async () => {
    try {
      const params = activeWarehouseId ? `?warehouse_id=${activeWarehouseId}` : '';
      const data = await apiClient.get<Mission[]>(`/api/missions${params}`);
      setMgmtMissions(data);
      setMgmtError(null);
    } catch (e) {
      setMgmtError(e instanceof ApiError ? e.message : 'Failed to load missions.');
    }
  };

  const loadScanSummary = async () => {
    try {
      const url = activeWarehouseId
        ? `/api/inventory/missions?warehouse_id=${activeWarehouseId}`
        : '/api/inventory/missions';
      const data = await apiClient.get<MissionSummary[]>(url);
      setScanSummary(data);
    } catch { /* non-fatal */ }
  };

  useEffect(() => {
    loadMgmt();
    loadScanSummary();
    intervalRef.current = setInterval(() => { loadMgmt(); loadScanSummary(); }, 30_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      wsRef.current?.close();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWarehouseId]);

  const connectWs = () => {
    if (!sessionId.trim()) return;
    wsRef.current?.close();
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${window.location.host}/ws/pilot/${sessionId.trim()}`);
    ws.onopen  = () => setWsConnected(true);
    ws.onclose = () => setWsConnected(false);
    ws.onerror = () => setWsConnected(false);
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data) as { event: string; photo_id?: string; data?: Record<string, unknown> };
        if (msg.event === 'PING') return;
        setLiveEvents(prev => [{ ...msg, receivedAt: new Date().toISOString() }, ...prev].slice(0, 50));
        if (msg.event === 'SCAN_RESULT' || msg.event === 'DETECTION_FAILURE') {
          loadMgmt(); loadScanSummary();
        }
      } catch { /* malformed frame — ignore */ }
    };
    wsRef.current = ws;
  };

  const disconnectWs = () => { wsRef.current?.close(); setWsConnected(false); setLiveEvents([]); };

  const totalScanned = scanSummary?.reduce((s, m) => s + m.record_count, 0) ?? 0;

  return (
    <div className="mgr-missions">

      {/* ── Tab bar ── */}
      <div className="mgr-tab-bar">
        <button
          className={`mgr-tab ${tab === 'list' ? 'mgr-tab--active' : ''}`}
          onClick={() => setTab('list')}
        >
          Mission List
          {mgmtMissions !== null && (
            <span className="mgr-tab-count">{mgmtMissions.length}</span>
          )}
        </button>
        <button
          className={`mgr-tab ${tab === 'create' ? 'mgr-tab--active' : ''}`}
          onClick={() => setTab('create')}
        >
          + New Mission
        </button>
        <button className="btn btn--ghost btn--sm mgr-tab-refresh" onClick={() => { loadMgmt(); loadScanSummary(); }}>
          ↻ Refresh
        </button>
      </div>

      {/* ── Create tab ── */}
      {tab === 'create' && (
        <CreateMission onCreated={() => { loadMgmt(); setTab('list'); }} />
      )}

      {/* ── List tab ── */}
      {tab === 'list' && (
        <>
          {mgmtError && <div className="form-error">{mgmtError}</div>}

          {mgmtMissions === null && !mgmtError && (
            <div className="mgr-user-count">Loading missions…</div>
          )}

          {mgmtMissions?.length === 0 && (
            <div className="mgr-placeholder">
              <span className="mgr-placeholder-icon">🗺</span>
              <p>No missions yet. Create one using the <b>+ New Mission</b> tab above.</p>
            </div>
          )}

          {mgmtMissions && mgmtMissions.length > 0 && (
            <>
              {/* Pending review alert */}
              {(() => {
                const totalPending = scanSummary?.reduce((n, s) => n + s.review_count, 0) ?? 0;
                return totalPending > 0 ? (
                  <div className="mgr-review-alert">
                    ⚠️ <strong>{totalPending}</strong> location{totalPending !== 1 ? 's' : ''} pending manager review across active missions — expand a mission below to approve or reject.
                  </div>
                ) : null;
              })()}
              <div className="mgr-mission-list">
                {mgmtMissions.map(m => {
                  const summary = scanSummary?.find(s => s.mission_id === m.id) ?? null;
                  return (
                    <MissionRow
                      key={m.id}
                      mission={m}
                      scanSummary={summary}
                      onStatusChange={loadMgmt}
                    />
                  );
                })}
              </div>
            </>
          )}

          {/* ── Scan stats summary ── */}
          {totalScanned > 0 && (
            <div className="mgr-payment-summary" style={{ marginTop: '1.5rem' }}>
              <div className="mgr-payment-total">
                <span className="mgr-payment-number">{totalScanned.toLocaleString()}</span>
                <div className="mgr-payment-labels">
                  <span className="mgr-payment-title">Total locations scanned</span>
                  <span className="mgr-payment-sub">
                    across {scanSummary?.length ?? 0} mission{(scanSummary?.length ?? 0) !== 1 ? 's' : ''} with scan data
                  </span>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Live scan feed (always visible) ── */}
      <div className="mgr-live-feed" style={{ marginTop: '1.5rem' }}>
        <h3 className="mgr-subsection-title">Live Scan Feed</h3>
        <p className="mgr-section-desc">
          Enter an active pilot session ID to monitor scan events in real time.
        </p>
        <div className="mgr-live-connect">
          <input
            className="form-input"
            placeholder="Pilot session ID"
            value={sessionId}
            onChange={e => setSessionId(e.target.value)}
            disabled={wsConnected}
          />
          {wsConnected
            ? <button className="btn btn--ghost btn--sm" onClick={disconnectWs}>Disconnect</button>
            : <button className="btn btn--primary btn--sm" onClick={connectWs} disabled={!sessionId.trim()}>Connect</button>
          }
          <span className={`mgr-ws-badge ${wsConnected ? 'mgr-ws-badge--on' : 'mgr-ws-badge--off'}`}>
            {wsConnected ? '● Live' : '○ Offline'}
          </span>
        </div>

        {liveEvents.length > 0 && (
          <div className="mgr-live-events">
            {liveEvents.map((ev, i) => (
              <div key={i} className={`mgr-live-event mgr-live-event--${ev.event.toLowerCase()}`}>
                <span className="mgr-live-time">{new Date(ev.receivedAt).toLocaleTimeString()}</span>
                <span className={`mgr-chip ${FLAG_CHIP[ev.event] ?? ''}`}>{ev.event}</span>
                {ev.photo_id && <span className="mgr-live-id">photo {ev.photo_id.slice(0, 8)}…</span>}
                {(ev.data?.out_of_scope as boolean) && (
                  <span className="mgr-chip mgr-chip--super-admin">⚠ OUT OF SCOPE</span>
                )}
                {ev.event === 'SCAN_RESULT' && ev.data && (
                  <span className="mgr-live-detail">
                    {(ev.data.barcodes_found as number) ?? 0} barcodes ·{' '}
                    {(ev.data.layers_detected as number) ?? 0} layers
                  </span>
                )}
                {ev.event === 'DETECTION_FAILURE' && ev.data && (
                  <span className="mgr-td--err">{String(ev.data.reason ?? 'no detection')}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Analytics() {
  const { activeWarehouseId }               = useWarehouse();
  const [missions, setMissions]             = useState<MissionSummary[] | null>(null);
  const [mgmtMissions, setMgmtMissions]     = useState<Mission[]>([]);
  const [error, setError]                   = useState<string | null>(null);
  const [heatmapMission, setHeatmapMission] = useState('');
  const [aisleRows, setAisleRows]           = useState<AisleRow[] | null>(null);
  const [heatmapBusy, setHeatmapBusy]       = useState(false);
  const [heatmapError, setHeatmapError]     = useState<string | null>(null);

  useEffect(() => {
    const scanUrl = activeWarehouseId
      ? `/api/inventory/missions?warehouse_id=${activeWarehouseId}`
      : '/api/inventory/missions';
    const mgmtUrl = activeWarehouseId
      ? `/api/missions?warehouse_id=${activeWarehouseId}`
      : '/api/missions';
    setMissions(null);
    setHeatmapMission('');
    setAisleRows(null);
    // Load both in parallel
    Promise.all([
      apiClient.get<MissionSummary[]>(scanUrl),
      apiClient.get<Mission[]>(mgmtUrl).catch(() => [] as Mission[]),
    ])
      .then(([scanData, mgmtData]) => {
        setMissions(scanData);
        setMgmtMissions(mgmtData);
      })
      .catch(e => setError(e instanceof ApiError ? e.message : 'Failed to load analytics data.'));
  // Re-run when warehouse scope changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWarehouseId]);

  /** Return a mission title given its scan-summary mission_id */
  const missionTitle = (missionId: string) => {
    const mgmt = mgmtMissions.find(m => m.id === missionId);
    return mgmt?.title ?? `${missionId.slice(0, 8)}…`;
  };

  const loadAisleHeatmap = async () => {
    if (!heatmapMission) return;
    setHeatmapBusy(true); setHeatmapError(null); setAisleRows(null);
    try {
      const data = await apiClient.get<MissionRecordsResponse>(
        `/api/inventory/missions/${heatmapMission}/records`,
      );
      setAisleRows([...data.aisles].sort((a, b) => b.discrepancy_count - a.discrepancy_count));
    } catch (e) {
      setHeatmapError(e instanceof ApiError ? e.message : 'Failed to load mission records.');
    } finally {
      setHeatmapBusy(false);
    }
  };

  // Aggregate totals across all missions
  const totals = missions?.reduce(
    (acc, m) => ({
      match:       acc.match       + m.match_count,
      discrepancy: acc.discrepancy + m.discrepancy_count,
      mismatch:    acc.mismatch    + m.mismatch_count,
      retake:      acc.retake      + m.retake_count,
      review:      acc.review      + m.review_count,
      records:     acc.records     + m.record_count,
    }),
    { match: 0, discrepancy: 0, mismatch: 0, retake: 0, review: 0, records: 0 },
  );

  const flagTotal = totals
    ? totals.match + totals.discrepancy + totals.mismatch + totals.retake + totals.review
    : 0;

  // Sorted by last_updated descending for the quality trend
  const sortedMissions = missions
    ? [...missions].sort((a, b) => {
        if (!a.last_updated) return 1;
        if (!b.last_updated) return -1;
        return new Date(b.last_updated).getTime() - new Date(a.last_updated).getTime();
      })
    : [];

  return (
    <div className="mgr-analytics">
      {error && <div className="form-error">{error}</div>}
      {missions === null && !error && <div className="mgr-user-count">Loading analytics…</div>}

      {/* ── 0. KPI summary strip ── */}
      {missions && missions.length > 0 && totals && (
        <section className="mgr-analytics-section">
          <div className="mgr-kpi-strip">
            <div className="mgr-kpi-cell">
              <span className="mgr-kpi-num">{missions.length}</span>
              <span className="mgr-kpi-lbl">Missions</span>
            </div>
            <div className="mgr-kpi-cell">
              <span className="mgr-kpi-num">{totals.records.toLocaleString()}</span>
              <span className="mgr-kpi-lbl">Locations scanned</span>
            </div>
            <div className="mgr-kpi-cell">
              <span className="mgr-kpi-num">
                {totals.records > 0 ? Math.round((totals.match / totals.records) * 100) : 0}%
              </span>
              <span className="mgr-kpi-lbl">Overall match rate</span>
            </div>
            <div className={`mgr-kpi-cell ${totals.retake > 0 ? 'mgr-kpi-cell--warn' : ''}`}>
              <span className="mgr-kpi-num">{totals.retake}</span>
              <span className="mgr-kpi-lbl">Pending retakes</span>
            </div>
            <div className={`mgr-kpi-cell ${totals.review > 0 ? 'mgr-kpi-cell--alert' : ''}`}>
              <span className="mgr-kpi-num">{totals.review}</span>
              <span className="mgr-kpi-lbl">Awaiting review</span>
            </div>
          </div>
        </section>
      )}

      {/* ── 1. Scan Quality Trend ── */}
      {missions && (
        <section className="mgr-analytics-section">
          <h3 className="mgr-subsection-title">Scan Quality Trend</h3>
          <p className="mgr-section-desc" style={{ marginBottom: 0 }}>
            Match rate per mission, most recent first.
          </p>
          {sortedMissions.length === 0 ? (
            <div className="mgr-placeholder">
              <span className="mgr-placeholder-icon">📊</span>
              <p>No mission data yet. Complete a scan to see results here.</p>
            </div>
          ) : (
            <div className="mgr-analytics-bars">
              {sortedMissions.map(m => {
                const pct = m.record_count > 0
                  ? Math.round((m.match_count / m.record_count) * 100) : 0;
                const barCls = pct >= 90 ? 'mgr-bar--good' : pct >= 70 ? 'mgr-bar--warn' : 'mgr-bar--bad';
                return (
                  <div key={m.mission_id} className="mgr-bar-row">
                    <div className="mgr-bar-label">
                      <span className="mgr-bar-title">{missionTitle(m.mission_id)}</span>
                      <span className="mgr-bar-meta">
                        {m.last_updated ? new Date(m.last_updated).toLocaleDateString() : '—'}
                        {' · '}{m.record_count} records
                        {m.review_count > 0 && <span className="mgr-bar-review-badge"> · {m.review_count} pending review</span>}
                        {m.retake_count  > 0 && <span className="mgr-bar-retake-badge"> · {m.retake_count} retake</span>}
                      </span>
                    </div>
                    <div className="mgr-bar-track">
                      <div className={`mgr-bar-fill ${barCls}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="mgr-bar-pct">{pct}%</span>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* ── 2. Telemetry Flag Ratio ── */}
      {totals && flagTotal > 0 && (
        <section className="mgr-analytics-section">
          <h3 className="mgr-subsection-title">Telemetry Flag Ratio</h3>
          <p className="mgr-section-desc" style={{ marginBottom: 0 }}>
            Aggregate flag breakdown across {missions!.length} mission{missions!.length !== 1 ? 's' : ''} · {totals.records.toLocaleString()} total records.
          </p>
          <div className="mgr-flag-bars">
            {(
              [
                { label: 'Match',        count: totals.match,       cls: 'mgr-bar--good' },
                { label: 'Discrepancy',  count: totals.discrepancy, cls: 'mgr-bar--bad'  },
                { label: 'Mismatch SKU', count: totals.mismatch,    cls: 'mgr-bar--warn' },
                { label: 'Retake',       count: totals.retake,      cls: 'mgr-bar--warn' },
                { label: 'Review',       count: totals.review,      cls: 'mgr-bar--warn' },
              ] as { label: string; count: number; cls: string }[]
            ).filter(f => f.count > 0).map(({ label, count, cls }) => {
              const pct = Math.round((count / flagTotal) * 100);
              return (
                <div key={label} className="mgr-bar-row">
                  <div className="mgr-bar-label">
                    <span>{label}</span>
                    <span className="mgr-bar-meta">{count.toLocaleString()} scans</span>
                  </div>
                  <div className="mgr-bar-track">
                    <div className={`mgr-bar-fill ${cls}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="mgr-bar-pct">{pct}%</span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── 3. Aisle Error Heatmap ── */}
      <section className="mgr-analytics-section">
        <h3 className="mgr-subsection-title">Aisle Error Heatmap</h3>
        <p className="mgr-section-desc" style={{ marginBottom: 0 }}>
          Aisles ranked by discrepancy count. Red = high error rate, green = clean.
        </p>
        <div className="mgr-live-connect">
          <select
            className="form-input"
            value={heatmapMission}
            onChange={e => { setHeatmapMission(e.target.value); setAisleRows(null); setHeatmapError(null); }}
            disabled={heatmapBusy || !missions}
          >
            <option value="">— Select a mission —</option>
            {(missions ?? []).map(m => (
              <option key={m.mission_id} value={m.mission_id}>
                {missionTitle(m.mission_id)}
                {m.last_updated ? ` · ${new Date(m.last_updated).toLocaleDateString()}` : ''}
                {` · ${m.record_count} records`}
              </option>
            ))}
          </select>
          <button
            className="btn btn--primary btn--sm"
            onClick={loadAisleHeatmap}
            disabled={!heatmapMission || heatmapBusy}
          >
            {heatmapBusy ? 'Loading…' : 'Load'}
          </button>
        </div>

        {heatmapError && <div className="form-error">{heatmapError}</div>}

        {aisleRows !== null && aisleRows.length === 0 && (
          <p className="mgr-section-desc">No aisle breakdown found for this mission.</p>
        )}

        {aisleRows && aisleRows.length > 0 && (
          <div className="mgr-heatmap">
            {aisleRows.map(row => {
              const errPct = row.total_count > 0
                ? Math.round((row.discrepancy_count / row.total_count) * 100) : 0;
              // hsl: 0° = full red, 120° = full green
              const hue = Math.round((1 - errPct / 100) * 120);
              return (
                <div key={row.aisle} className="mgr-heatmap-row">
                  <span className="mgr-heatmap-label">{row.aisle}</span>
                  <div className="mgr-heatmap-track">
                    <div
                      className="mgr-heatmap-fill"
                      style={{ width: `${Math.max(errPct, 2)}%`, background: `hsl(${hue},75%,42%)` }}
                    />
                  </div>
                  <span className="mgr-heatmap-stats">
                    {row.discrepancy_count} errors / {row.total_count} · {errPct}%
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

// ── Warehouses (v2) ──────────────────────────────────────────────────────────

export function Warehouses() {
  const { user } = useAuth();
  const { warehouses, refreshWarehouses } = useWarehouse();
  const rows = warehouses;  // WarehouseItem[] — carries location_count + aisles
  const canDelete = user?.role === 'manager' || user?.role === 'admin' || user?.role === 'super_admin';

  // ── Create form ──
  const [form, setForm]           = useState({ name: '', code: '', location: '' });
  const [locFile, setLocFile]     = useState<File | null>(null);
  const locFileRef                = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [success, setSuccess]     = useState<string | null>(null);
  const [dupWarning, setDupWarning] = useState<{ count: number; codes: string[] } | null>(null);

  // ── Inline edit ──
  const [editId, setEditId]     = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', location: '' });
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // ── Toggle / deactivate ──
  const [togglingId, setTogglingId]     = useState<string | null>(null);
  const [deletingId,  setDeletingId]    = useState<string | null>(null);

  const setF = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const autoCode = (name: string) =>
    name.toUpperCase().replace(/[^A-Z0-9]+/g, '-').replace(/^-|-$/g, '');

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setForm(f => ({ ...f, name, code: f.code === autoCode(f.name) ? autoCode(name) : f.code }));
  };

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!locFile) { setError('A location master file is required.'); return; }
    setBusy(true); setError(null); setSuccess(null); setDupWarning(null);
    try {
      const fd = new FormData();
      fd.append('name',     form.name.trim());
      fd.append('code',     form.code.trim().toUpperCase());
      if (form.location.trim()) fd.append('location', form.location.trim());
      fd.append('locations_file', locFile);
      const created = await apiClient.postForm<WarehouseItem>('/api/manage/warehouses', fd);
      setSuccess(
        `Warehouse '${created.code}' created — ` +
        `${created.location_count.toLocaleString()} locations loaded across ` +
        `${created.aisles.length} aisle${created.aisles.length !== 1 ? 's' : ''}.`
      );
      if ((created.duplicate_count ?? 0) > 0) {
        setDupWarning({ count: created.duplicate_count!, codes: created.duplicate_codes ?? [] });
      }
      setForm({ name: '', code: '', location: '' });
      setLocFile(null);
      if (locFileRef.current) locFileRef.current.value = '';
      await refreshWarehouses();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create warehouse.');
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (w: WarehouseItem) => {
    setEditId(w.id);
    setEditForm({ name: w.name, location: w.location ?? '' });
    setEditError(null);
  };

  const saveEdit = async (id: string) => {
    setEditBusy(true); setEditError(null);
    try {
      await apiClient.patch(`/api/manage/warehouses/${id}`, {
        name:     editForm.name.trim(),
        location: editForm.location.trim() || null,
      });
      setEditId(null);
      await refreshWarehouses();
    } catch (err) {
      setEditError(err instanceof ApiError ? err.message : 'Failed to save.');
    } finally {
      setEditBusy(false);
    }
  };

  const toggleActive = async (w: WarehouseItem) => {
    setTogglingId(w.id);
    try {
      await apiClient.patch(`/api/manage/warehouses/${w.id}`, { is_active: !w.is_active });
      await refreshWarehouses();
    } catch { /* silently ignore */ } finally {
      setTogglingId(null);
    }
  };

  const permanentlyDelete = async (w: WarehouseItem) => {
    const confirmed = confirm(
      `⚠ PERMANENTLY DELETE "${w.name}" (${w.code})?\n\n` +
      `This will irreversibly remove ALL data for this warehouse including:\n` +
      `  • ${w.location_count.toLocaleString()} location records\n` +
      `  • All SOH snapshots, SKU rules, missions, and scan results\n\n` +
      `This action cannot be undone. Type OK to confirm.`
    );
    if (!confirmed) return;
    setDeletingId(w.id);
    try {
      await apiClient.delete(`/api/manage/warehouses/${w.id}`);
      await refreshWarehouses();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'Failed to delete warehouse.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="mgr-users-wrap">

      {/* ── List ── */}
      <div className="mgr-user-count">
        <b>{rows.length}</b> warehouse{rows.length !== 1 ? 's' : ''}
      </div>

      {rows.length > 0 && (
        <div className="mgr-user-table-wrap" style={{ maxHeight: 400 }}>
          <table className="mgr-user-table">
            <thead>
              <tr>
                <th>Name</th><th>Code</th><th>Location</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(w => (
                <tr key={w.id} className={w.is_active ? '' : 'mgr-row--disabled'}>
                  <td>
                    {editId === w.id
                      ? <input className="form-input form-input--inline" value={editForm.name}
                          onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                          disabled={editBusy} autoFocus />
                      : <strong>{w.name}</strong>}
                  </td>
                  <td><code className="mgr-mission-id">{w.code}</code></td>
                  <td>
                    {editId === w.id
                      ? <input className="form-input form-input--inline" value={editForm.location}
                          onChange={e => setEditForm(f => ({ ...f, location: e.target.value }))}
                          disabled={editBusy} placeholder="Address / site description" />
                      : <span style={{ color: w.location ? 'inherit' : 'var(--clr-txt-muted)' }}>
                          {w.location ?? '—'}
                        </span>}
                  </td>
                  <td>
                    <div className={w.is_active ? 'mgr-td--ok' : 'mgr-td--err'} style={{ fontWeight: 600, marginBottom: 2 }}>
                      {w.is_active ? 'Active' : 'Inactive'}
                    </div>
                    {w.location_count > 0 ? (
                      <div className="wh-loc-summary">
                        <span className="wh-loc-count">
                          {w.location_count.toLocaleString()} locations
                          {w.aisles.length > 0 && (
                            <> · {w.aisles.length} aisle{w.aisles.length !== 1 ? 's' : ''}</>
                          )}
                        </span>
                        {w.aisles.length > 0 && (
                          <span className="wh-loc-aisles">
                            Aisles: {w.aisles.join(', ')}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="wh-loc-summary wh-loc-summary--none">
                        No locations uploaded
                      </div>
                    )}
                  </td>
                  <td className="mgr-wh-actions">
                    {editId === w.id ? (
                      <>
                        <button className="mgr-toggle-btn mgr-toggle-btn--enable"
                          onClick={() => saveEdit(w.id)} disabled={editBusy || !editForm.name.trim()}>
                          {editBusy ? '…' : 'Save'}
                        </button>
                        <button className="mgr-toggle-btn mgr-toggle-btn--disable"
                          onClick={() => setEditId(null)} disabled={editBusy}>Cancel</button>
                        {editError && <span className="mgr-td--err" style={{ fontSize: '.8rem' }}>{editError}</span>}
                      </>
                    ) : (
                      <>
                        <button className="mgr-toggle-btn" style={{ borderColor: 'var(--clr-border)' }}
                          onClick={() => startEdit(w)}>Edit</button>
                        <button
                          className={`mgr-toggle-btn ${w.is_active ? 'mgr-toggle-btn--disable' : 'mgr-toggle-btn--enable'}`}
                          onClick={() => toggleActive(w)} disabled={togglingId === w.id}>
                          {togglingId === w.id ? '…' : w.is_active ? 'Disable' : 'Enable'}
                        </button>
                        {/* Permanently delete — only for INACTIVE warehouses, manager/admin only */}
                        {!w.is_active && canDelete && (
                          <button
                            className="mgr-toggle-btn mgr-wh-delete-btn"
                            onClick={() => permanentlyDelete(w)}
                            disabled={deletingId === w.id}
                            title="Permanently delete this warehouse and all its data"
                          >
                            {deletingId === w.id ? '…' : '🗑 Delete'}
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create form ── */}
      <h3 className="mgr-subsection-title">Add new warehouse</h3>
      <form className="mgr-section-form" onSubmit={create}>
        <div className="mgr-form-row">
          <div className="form-field">
            <label className="form-label">Warehouse Name *</label>
            <input className="form-input" value={form.name} onChange={handleNameChange}
              placeholder="e.g. DHL Fonterra Singapore" required disabled={busy} />
          </div>
          <div className="form-field">
            <label className="form-label">
              Code * <span style={{ color: 'var(--clr-txt-muted)', fontWeight: 400 }}>(unique, auto-uppercased)</span>
            </label>
            <input className="form-input" value={form.code}
              onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
              placeholder="e.g. DHL-FONTERRA-SG" required disabled={busy} />
          </div>
        </div>
        <div className="form-field">
          <label className="form-label">
            Location / Address <span style={{ color: 'var(--clr-txt-muted)', fontWeight: 400 }}>(optional)</span>
          </label>
          <input className="form-input" value={form.location} onChange={setF('location')}
            placeholder="e.g. 12 Harbour Drive, Jurong, Singapore 618923" disabled={busy} />
        </div>
        <div className="form-field">
          <label className="form-label">
            Location Master File *
            <span style={{ color: 'var(--clr-txt-muted)', fontWeight: 400 }}>
              {' '}— CSV or Excel, first column = location codes (e.g. 10-001-B, AA-003-C)
            </span>
          </label>
          <input
            ref={locFileRef}
            className="form-input"
            type="file"
            accept=".xlsx,.xls,.xlsm,.csv"
            onChange={e => setLocFile(e.target.files?.[0] ?? null)}
            disabled={busy}
            required
          />
          {locFile && (
            <div className="wh-file-preview">
              📄 {locFile.name} ({(locFile.size / 1024).toFixed(0)} KB)
            </div>
          )}
        </div>
        {error   && <div className="form-error">{error}</div>}
        {success && <div className="form-success">{success}</div>}
        {dupWarning && (
          <div className="wh-dup-warning">
            <div className="wh-dup-warning-header">
              ⚠ {dupWarning.count} duplicate location code{dupWarning.count !== 1 ? 's' : ''} found in the file — merged into one entry each.
            </div>
            {dupWarning.codes.length > 0 && (
              <details className="wh-dup-details">
                <summary>Show duplicate codes ({dupWarning.codes.length}{dupWarning.codes.length === 50 ? '+' : ''})</summary>
                <div className="wh-dup-codes">
                  {dupWarning.codes.map(c => (
                    <code key={c} className="wh-dup-code">{c}</code>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
        <button className="btn btn--primary" type="submit"
          disabled={busy || !form.name.trim() || !form.code.trim() || !locFile}>
          {busy ? 'Creating…' : 'Create Warehouse'}
        </button>
      </form>
    </div>
  );
}

function IngestionResult({ data }: { data: IngestionSummary }) {
  return (
    <div className="mgr-ingest-result">
      <div className="mgr-ingest-stats">
        <span className="mgr-stat"><b>{data.rows_parsed}</b> parsed</span>
        <span className="mgr-stat mgr-stat--ok"><b>{data.rows_accepted}</b> accepted</span>
        {data.rows_inserted > 0 && <span className="mgr-stat mgr-stat--ok"><b>{data.rows_inserted}</b> inserted</span>}
        {(data.rows_updated ?? 0) > 0 && <span className="mgr-stat mgr-stat--ok"><b>{data.rows_updated}</b> updated</span>}
        {data.rows_rejected > 0  && <span className="mgr-stat mgr-stat--err"><b>{data.rows_rejected}</b> rejected</span>}
      </div>
      {data.validation_errors && data.validation_errors.length > 0 && (
        <details className="mgr-ingest-errors">
          <summary>Validation errors ({data.validation_errors.length})</summary>
          <ul>
            {data.validation_errors.slice(0, 20).map((e, i) => (
              <li key={i}>Row {e.row} · <b>{e.field}</b>: {e.message}</li>
            ))}
            {data.validation_errors.length > 20 && <li>…and {data.validation_errors.length - 20} more</li>}
          </ul>
        </details>
      )}
    </div>
  );
}

// ── Section config ────────────────────────────────────────────────────────────

// SOH upload lives in the MISSION flow (Missions → SOH per mission, 0044) —
// a snapshot is a point-in-time capture paired with a mission's photos, not
// warehouse master data. Only the SKU master stays warehouse-scoped here.
const SECTIONS: { id: string; icon: string; title: string; desc: string; content: React.ReactNode; adminOnly?: boolean }[] = [
  { id: 'sku',        icon: '📦', title: 'SKU Master',  desc: 'Upload SKU Ti/Hi dimensions file',    content: <SkuUpload /> },
  { id: 'missions',   icon: '🗺', title: 'Missions',    desc: 'Create and manage audit missions',      content: <MissionPage /> },
  { id: 'analytics',  icon: '📈', title: 'Analytics',   desc: 'Scan health metrics and aisle heatmap', content: <Analytics /> },
  { id: 'users',      icon: '👥', title: 'Users',       desc: 'Manage pilot and customer accounts',  content: <CreateUser /> },
  { id: 'warehouses', icon: '🏭', title: 'Warehouses',  desc: 'Create and manage warehouse sites',    content: <WarehouseManagement /> },
  { id: 'detection',  icon: '🔬', title: 'Detection Lab', desc: 'Tune and test the QR detection pipeline', content: <DetectionLab />, adminOnly: true },
  { id: 'carton-lab',       icon: '🧮', title: 'Carton Lab',        desc: 'Test the TI/HI box counting pipeline',                                    content: <CartonLab />,        adminOnly: true },
  { id: 'customer-access', icon: '🔐', title: 'Warehouse Access',  desc: 'Control which warehouses each customer or manager can view',                content: <CustomerAccess />,   adminOnly: true },
  { id: 'lem',             icon: '🔖', title: 'QR Worklist',       desc: 'Find & verify physical QR-label faults from the latest stocktake',          content: <LemWorklist />,      adminOnly: true },
];

// ── Main component ────────────────────────────────────────────────────────────

export default function ManagerConsole() {
  const { user, signOut } = useAuth();
  const [activeId, setActiveId] = useState<string | null>(null);

  // Detection Lab (and any future adminOnly section) is hidden from managers;
  // the backend independently enforces RequireAdmin on its endpoints.
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const visibleSections = SECTIONS.filter(s => !s.adminOnly || isAdmin);

  const active = visibleSections.find(s => s.id === activeId) ?? null;

  return (
    <div className="mgr-page">
      <header className="mgr-header">
        <div className="mgr-header-left">
          <InfiniumMark variant="dark" height={34} />
          <h1 className="mgr-header-title">Operations Console</h1>
          <span className="mgr-role-chip">{user?.role}</span>
        </div>
        <div className="mgr-header-right">
          <WarehouseSwitcher />
          <span className="mgr-user">{user?.name || user?.email}</span>
          <button className="btn btn--ghost btn--sm" onClick={signOut}>Sign out</button>
        </div>
      </header>

      <main className="mgr-body">
        {active ? (
          <div className="mgr-section">
            <button className="mgr-back-btn" onClick={() => setActiveId(null)}>
              ← Back
            </button>
            <h2 className="mgr-section-title">
              <span>{active.icon}</span> {active.title}
            </h2>
            <p className="mgr-section-desc">{active.desc}</p>
            <div className="mgr-section-body">
              {active.content}
            </div>
          </div>
        ) : (
          <>
            <MainDashboard />
            <div className="mgr-tiles">
              {visibleSections.map((s) => (
                <button key={s.id} className="mgr-tile" onClick={() => setActiveId(s.id)}>
                  <span className="mgr-tile-icon">{s.icon}</span>
                  <h3 className="mgr-tile-title">{s.title}</h3>
                  <p className="mgr-tile-desc">{s.desc}</p>
                </button>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
